import InputField from "components/styledFormElements/InputField"
import IconDropdown from "components/styledFormElements/IconDropdown"
import { useGetIcons } from "hooks/useIconMaster"
import { useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { getImageSrc, getRelativeIconPath, normalizeIconPath } from "services/util"
import { X } from "lucide-react"

const SearchPageConfig = () => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<any>()
    const searchPageConfigErrors = errors.search_page_config as any

    const { data: iconsResponse } = useGetIcons()

    const icons = useMemo(
        () =>
            Array.isArray(iconsResponse?.data?.results)
                ? iconsResponse.data.results
                : [],
        [iconsResponse]
    )

    const toComparableIconPath = (value?: string | null) => {
        const relative = getRelativeIconPath(value || '')
        return normalizeIconPath(relative)
    }

    const getSelectedIconByValue = (value: string | number | null | undefined) => {
        if (!value) return null

        if (typeof value === 'number') {
            return icons.find((icon: any) => icon.id === value) || null
        }

        const normalizedValue = toComparableIconPath(String(value))

        return (
            icons.find((icon: any) => {
                const iconCandidates = [
                    icon?.icon,
                    icon?.path,
                    icon?.image,
                ]
                    .filter(Boolean)
                    .map((candidate: string) => toComparableIconPath(candidate))

                return iconCandidates.includes(normalizedValue)
            }) || null
        )
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                    label="Search Home Page Title"
                    type="text"
                    required
                    maxLength={100}
                    register={register("search_page_config.search_home_page_title", {
                        required: "Search Home Page Title is required",
                        maxLength: {
                            value: 100,
                            message: "Maximum 100 characters allowed"
                        }
                    })}
                    error={searchPageConfigErrors?.search_home_page_title?.message as string}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                    <h5 className="font-medium mb-4">Tool Section</h5>
                    <div className="space-y-4">
                        <InputField
                            label="Title"
                            type="text"
                            required
                            maxLength={100}
                            register={register("search_page_config.tool_section.title", {
                                required: "Tool section title is required",
                                maxLength: {
                                    value: 100,
                                    message: "Maximum 100 characters allowed"
                                }
                            })}
                            error={searchPageConfigErrors?.tool_section?.title?.message as string}
                        />
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300 block">
                                Icon <span className="text-red-700">*</span>
                            </label>
                            <Controller
                                name="search_page_config.tool_section.icon"
                                control={control}
                                rules={{ required: "Tool section icon is required" }}
                                render={({ field }) => {
                                    // const selectedIcon = getSelectedIconByValue(field.value)
                                    const iconValue = field.value;

                                    return (
                                        <div className="space-y-2">
                                            {/* <IconDropdown
                                                value={selectedIcon?.id || null}
                                                onChange={(selected: any) => {
                                                    const iconKey =
                                                        selected?.path ||
                                                        selected?.image ||
                                                        selected?.icon ||
                                                        ''
                                                    field.onChange(toComparableIconPath(iconKey))
                                                }}
                                            /> */}
                                            {iconValue ? (
                                                <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={
                                                                iconValue.startsWith('http')
                                                                    ? iconValue
                                                                    : getImageSrc(iconValue)
                                                            }
                                                            alt="Selected icon"
                                                            className="w-6 h-6 object-contain"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-200">
                                                            Icon selected
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            field.onChange('');
                                                        }}
                                                        className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            )
                                                : (
                                                    <IconDropdown
                                                        value={iconValue}
                                                        onChange={(selectedIcon: any) => {
                                                            const iconKey =
                                                                selectedIcon?.path ||
                                                                selectedIcon?.image ||
                                                                selectedIcon?.icon ||
                                                                '';

                                                            field.onChange(iconKey);
                                                        }}
                                                    />
                                                )}
                                        </div>
                                    )
                                }}
                            />
                            {searchPageConfigErrors?.tool_section?.icon?.message ? (
                                <p className="text-xs text-red-600 mt-1">
                                    {searchPageConfigErrors.tool_section.icon.message as string}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                    <h5 className="font-medium mb-4">Capsule Section</h5>
                    <div className="space-y-4">
                        <InputField
                            label="Title"
                            type="text"
                            required
                            maxLength={100}
                            register={register("search_page_config.capsule_section.title", {
                                required: "Capsule section title is required",
                                minLength: {
                                    value: 3,
                                    message: "Minimum 3 characters required"
                                },
                                maxLength: {
                                    value: 100,
                                    message: "Maximum 100 characters allowed"
                                }
                            })}
                            error={searchPageConfigErrors?.capsule_section?.title?.message as string}
                        />
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300 block">
                                Icon <span className="text-red-700">*</span>
                            </label>
                            <Controller
                                name="search_page_config.capsule_section.icon"
                                control={control}
                                rules={{ required: "Capsule section icon is required" }}
                                render={({ field }) => {
                                    const iconValue = field.value;

                                    // const selectedIcon = getSelectedIconByValue(field.value)
                                    // return (
                                    //     <div className="space-y-2">
                                    //         <IconDropdown
                                    //             value={selectedIcon?.id || null}
                                    //             onChange={(selected: any) => {
                                    //                 const iconKey =
                                    //                     selected?.path ||
                                    //                     selected?.image ||
                                    //                     selected?.icon ||
                                    //                     ''
                                    //                 field.onChange(toComparableIconPath(iconKey))
                                    //             }}
                                    //         />
                                    //     </div>
                                    // )
                                    return (
                                        <div className="space-y-2">
                                            {iconValue ? (
                                                <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={
                                                                iconValue.startsWith('http')
                                                                    ? iconValue
                                                                    : getImageSrc(iconValue)
                                                            }
                                                            alt="Selected icon"
                                                            className="w-6 h-6 object-contain"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-200">
                                                            Icon selected
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            field.onChange('');
                                                        }}
                                                        className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            )
                                                : (
                                                    <IconDropdown
                                                        value={iconValue}
                                                        onChange={(selectedIcon: any) => {
                                                            const iconKey =
                                                                selectedIcon?.path ||
                                                                selectedIcon?.image ||
                                                                selectedIcon?.icon ||
                                                                '';

                                                            field.onChange(iconKey);
                                                        }}
                                                    />
                                                )}
                                        </div>
                                    )
                                }}
                            />
                            {searchPageConfigErrors?.capsule_section?.icon?.message ? (
                                <p className="text-xs text-red-600 mt-1">
                                    {searchPageConfigErrors.capsule_section.icon.message as string}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SearchPageConfig
